/**
 * DashboardHeader Component
 * Top navigation with menu, FinList logo, and profile
 */

import React from 'react';
import { Menu, User, Cloud, CloudOff, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '@/contexts/SidebarContext';
// Feature 10: Sync Status Indicator
import { SyncStatusIcon } from '@/components/SyncStatusIndicator';

export default function DashboardHeader({ onMenuClick, syncStatus = 'synced' }) {
  const navigate = useNavigate();
  const { openSidebar } = useSidebar();
  const handleMenuButton = onMenuClick || openSidebar;

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b-2 border-gray-200 dark:border-slate-700 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Menu Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleMenuButton}
            className="p-1.5 sm:p-2 hover:bg-[#006D77]/10 rounded-xl transition-colors touch-manipulation"
            aria-label="Open menu"
          >
            <Menu size={20} className="text-[#006D77] sm:w-6 sm:h-6" />
          </motion.button>

          {/* Logo */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <img 
              src="/images/welcome/finlist-logo.png" 
              alt="FinList" 
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg"
            />
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#006D77]">
              FinList
            </h1>
            
            {/* Feature 10: Sync Status Icon - Always visible */}
            <SyncStatusIcon className="ml-2" />
          </div>

          {/* Help & Profile Buttons */}
          <div className="flex items-center gap-2">
            {/* Help Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/help')}
              className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-50 rounded-xl flex items-center justify-center hover:bg-emerald-100 transition-colors touch-manipulation"
              aria-label="Help & User Guide"
              title="Help & User Guide"
            >
              <HelpCircle size={18} className="text-emerald-600 sm:w-5 sm:h-5" />
            </motion.button>

            {/* Profile Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/profile')}
              className="w-9 h-9 sm:w-10 sm:h-10 bg-[#006D77]/10 rounded-xl flex items-center justify-center hover:bg-[#006D77]/20 transition-colors touch-manipulation"
              aria-label="Profile"
            >
              <User size={18} className="text-[#006D77] sm:w-5 sm:h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.header>
  );
}
